import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { createTableSortSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";

/** Advanced-filter name shared by the Table2 UI and the server resolver. */
export const WAREHOUSE_FLAGGED_PROBLEM_FLAGGED_AT_FILTER = "flaggedAt";

/** The only sortable column; other sort keys fall back to flagged-at. */
export const WAREHOUSE_FLAGGED_PROBLEM_SORT_KEY_FLAGGED_AT = "createdAt";

const warehouseFlaggedProblemSchema_ = z.object({
  id: z.string(),
  /** Display name of the user who flagged the problem. */
  flaggedBy: z.string().nullable(),
  /** Who last handled the subject: the return's processor or the order's shipper. */
  lastTouchedBy: z.string().nullable(),
  /** The merchant that owns the flagged order or return. */
  merchant: z.object({ teamId: z.string(), name: z.string().nullable() }),
  order: z.object({ id: z.string(), name: z.string().nullable() }).nullable(),
  lineItem: z
    .object({
      id: z.string(),
      title: z.string().nullable(),
      variantTitle: z.string().nullable(),
      imageUrl: z.string().nullable(),
    })
    .nullable(),
  /**
   * Inventory item the flag was pinned to, when identified. `id` is the
   * support-search identifier; `title` is the product title (null if undeclared).
   */
  inventoryItem: z
    .object({
      id: z.string(),
      title: z.string().nullable(),
      imageUrl: z.string().nullable(),
    })
    .nullable(),
  return: z.object({ id: z.string(), name: z.string() }).nullable(),
  returnItem: z
    .object({
      id: z.string(),
      title: z.string().nullable(),
      variantTitle: z.string().nullable(),
      sku: z.string().nullable(),
    })
    .nullable(),
  note: z.string().nullable(),
  createdAt: z.date(),
});
export type WarehouseFlaggedProblem = z.infer<
  typeof warehouseFlaggedProblemSchema_
>;

/**
 * Lists warehouse-flagged problems attributed to the requesting fulfillment
 * team, filtered via advancedFiltersData (flagged-at date).
 */
export const listWarehouseFlaggedProblems = {
  name: "listWarehouseFlaggedProblems" as const,
  input: z.object({
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
    sort: createTableSortSchema(z.string()).nullish(),
    pageNumber: z.number().int().nonnegative(),
    pageSize: z.number().int().positive().max(100),
  }),
  output: z.object({ problems: z.array(warehouseFlaggedProblemSchema_) }),
};

export const countWarehouseFlaggedProblems = {
  name: "countWarehouseFlaggedProblems" as const,
  input: z.object({
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ total: z.number() }),
};
